import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import EmojiPicker from 'emoji-picker-react';
import './ChatMessageInput.scss';

const ChatMessageInput = ({
    value,
    onChange,
    onSend,
    isSending,
    placeholder = 'Write a message...',
    isEditing = false,
    onCancelEdit,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showGifPicker, setShowGifPicker] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [gifResults, setGifResults] = useState([]);
    const [gifQuery, setGifQuery] = useState('');
    const [gifLoading, setGifLoading] = useState(false);
    const [gifError, setGifError] = useState(null);
    const attachmentsRef = useRef([]);

    const imageInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const documentInputRef = useRef(null);

    const textAreaRef = useRef(null);
    const emojiPopoverRef = useRef(null);
    const gifPopoverRef = useRef(null);
    const emojiTriggerRef = useRef(null);
    const gifTriggerRef = useRef(null);

    const resetAttachments = useCallback(() => {
        attachments.forEach((attachment) => {
            if (attachment.previewUrl) {
                URL.revokeObjectURL(attachment.previewUrl);
            }
        });
        setAttachments([]);
    }, [attachments]);

    useEffect(() => {
        attachmentsRef.current = attachments;
    }, [attachments]);

    useEffect(() => {
        if (isEditing) {
            resetAttachments();
        }
    }, [isEditing, resetAttachments]);

    const giphyApiKey = useMemo(() => {
        if (typeof window !== 'undefined' && window.octopusReactData?.giphyApiKey) {
            return window.octopusReactData.giphyApiKey;
        }
        if (typeof process !== 'undefined' && process.env.REACT_APP_GIPHY_API_KEY) {
            return process.env.REACT_APP_GIPHY_API_KEY;
        }
        return '';
    }, []);

    useEffect(() => {
        return () => {
            attachmentsRef.current.forEach((attachment) => {
                if (attachment.previewUrl) {
                    URL.revokeObjectURL(attachment.previewUrl);
                }
            });
        };
    }, []);

    const handleChange = useCallback(
        (event) => {
            onChange?.(event.target.value);
        },
        [onChange]
    );



    const createAttachment = useCallback((file, kind) => {
        const previewUrl =
            kind === 'image' ? URL.createObjectURL(file) : kind === 'video' ? URL.createObjectURL(file) : null;
        return {
            id: `${kind}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            file,
            kind,
            previewUrl,
            name: file.name,
        };
    }, []);

    const addAttachment = useCallback(
        (file, kind) => {
            if (!file) return;
            setAttachments((prev) => [...prev, createAttachment(file, kind)]);
        },
        [createAttachment]
    );

    const handleSend = useCallback(async () => {
        if (isSending || typeof onSend !== 'function') return;

        const payload = {
            text: value || '',
            attachments: attachments.map(({ file, kind }) => ({ file, kind })),
        };

        const result = await onSend(payload);

        if (result !== false) {
            onChange?.('');
            resetAttachments();
            setShowEmojiPicker(false);
            setShowGifPicker(false);
        }
    }, [attachments, isSending, onChange, onSend, resetAttachments, value]);

    const handleKeyDown = useCallback(
        (event) => {
            // if (event.key === 'Enter' && !event.shiftKey) {
            //     event.preventDefault();
            //    // handleSend();
            // }
        },
        [handleSend]
    );

    const handleFileSelection = useCallback(
        (event, kind) => {
            const { files } = event.target;
            if (!files || files.length === 0) return;

            Array.from(files).forEach((file) => addAttachment(file, kind));

            event.target.value = '';
        },
        [addAttachment]
    );

    const handleRemoveAttachment = useCallback((id) => {
        setAttachments((prev) => {
            const target = prev.find((item) => item.id === id);
            if (target?.previewUrl) {
                URL.revokeObjectURL(target.previewUrl);
            }
            return prev.filter((item) => item.id !== id);
        });
    }, []);

    const toggleEmojiPicker = useCallback(() => {
        setShowEmojiPicker((prev) => !prev);
        setShowGifPicker(false);
    }, []);

    const toggleGifPicker = useCallback(() => {
        setShowGifPicker((prev) => !prev);
        setShowEmojiPicker(false);
    }, []);

    const fetchGifs = useCallback(
        async ({ query = '', signal } = {}) => {
            if (!giphyApiKey) return;

            setGifLoading(true);
            setGifError(null);

            const endpoint = query
                ? `https://api.giphy.com/v1/gifs/search?api_key=${giphyApiKey}&q=${encodeURIComponent(
                      query
                  )}&limit=24&offset=0&rating=g&lang=en`
                : `https://api.giphy.com/v1/gifs/trending?api_key=${giphyApiKey}&limit=50&offset=0&rating=g`;

            try {
                const response = await fetch(endpoint, { signal });
                if (!response.ok) {
                    throw new Error(`Failed to fetch GIFs (status ${response.status})`);
                }
                const data = await response.json();
                setGifResults(Array.isArray(data?.data) ? data.data : []);
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('GIF fetch failed', error);
                    setGifError(error.message || 'Failed to load GIFs');
                }
            } finally {
                setGifLoading(false);
            }
        },
        [giphyApiKey]
    );

    useEffect(() => {
        if (!showGifPicker || !giphyApiKey) return;
        const controller = new AbortController();
        const timer = setTimeout(() => {
            fetchGifs({ query: gifQuery.trim(), signal: controller.signal });
        }, 250);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [showGifPicker, gifQuery, giphyApiKey, fetchGifs]);

    useEffect(() => {
        if (!showGifPicker) {
            setGifQuery('');
            setGifError(null);
        }
    }, [showGifPicker]);

    // Handle clicks outside emoji and GIF popovers
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is outside emoji popover and trigger
            if (
                showEmojiPicker &&
                emojiPopoverRef.current &&
                emojiTriggerRef.current &&
                !emojiPopoverRef.current.contains(event.target) &&
                !emojiTriggerRef.current.contains(event.target)
            ) {
                setShowEmojiPicker(false);
            }

            // Check if click is outside GIF popover and trigger
            if (
                showGifPicker &&
                gifPopoverRef.current &&
                gifTriggerRef.current &&
                !gifPopoverRef.current.contains(event.target) &&
                !gifTriggerRef.current.contains(event.target)
            ) {
                setShowGifPicker(false);
            }
        };

        // Only add listener if at least one popover is open
        if (showEmojiPicker || showGifPicker) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showEmojiPicker, showGifPicker]);

    const handleEmojiSelect = useCallback(
        (emoji) => {
            if (!emoji) return;

            const textarea = textAreaRef.current;
            const currentValue = value || '';

            if (!textarea) {
                onChange?.(`${currentValue}${emoji}`);
                setShowEmojiPicker(false);
                return;
            }

            const selectionStart = textarea.selectionStart ?? currentValue.length;
            const selectionEnd = textarea.selectionEnd ?? currentValue.length;

            const updatedValue =
                currentValue.slice(0, selectionStart) +
                emoji +
                currentValue.slice(selectionEnd);

            const caretPosition = selectionStart + emoji.length;

            onChange?.(updatedValue);
            setShowEmojiPicker(false);

            requestAnimationFrame(() => {
                textarea.focus();
                requestAnimationFrame(() => {
                    textarea.setSelectionRange(caretPosition, caretPosition);
                });
            });
        },
        [onChange, value]
    );

    const handleGifSelect = useCallback(
        async (gif) => {
            if (!gif) return;

            const gifUrl =
                gif.images?.original?.url ||
                gif.images?.downsized_medium?.url ||
                gif.images?.downsized?.url ||
                gif.url;

            if (!gifUrl) {
                console.warn('GIF payload missing url', gif);
                return;
            }

            try {
                const response = await fetch(gifUrl);
                const blob = await response.blob();
                const fileName = `${gif.id || 'gif'}-${Date.now()}.gif`;
                const file = new File([blob], fileName, { type: blob.type || 'image/gif' });
                addAttachment(file, 'image');
                setShowGifPicker(false);
            } catch (error) {
                console.error('Failed to attach GIF', error);
                setGifError('Failed to attach GIF. Please try another one.');
            }
        },
        [addAttachment]
    );

    const hasAttachments = attachments.length > 0;

    const groupedAttachments = useMemo(() => {
        return {
            images: attachments.filter((item) => item.kind === 'image'),
            videos: attachments.filter((item) => item.kind === 'video'),
            documents: attachments.filter((item) => item.kind === 'document'),
        };
    }, [attachments]);

    return (
        <div className="octopus-chat-input-wrapper">
            <div className={`octopus-chat-input-surface ${isExpanded ? 'expanded' : ''}`}>
                {isEditing && (
                    <div className="octopus-editing-banner">
                        <div className="octopus-editing-left">
                            <span className="octopus-editing-icon bb-icon-f bb-icon-edit" aria-hidden="true"></span>
                            <div className="octopus-editing-text">
                                <span className="octopus-editing-title">Edit message</span>
                                <span className="octopus-editing-preview" title={value || ''}>
                                    {value || 'Editing your message'}
                                </span>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="octopus-edit-cancel"
                            onClick={() => {
                                resetAttachments();
                                onCancelEdit?.();
                            }}
                            aria-label="Cancel editing"
                        >
                            <span aria-hidden="true">×</span>
                            <span className="octopus-sr-only">Cancel editing</span>
                        </button>
                    </div>
                )}
                {isSending && attachments.length > 0 && (
                    <div className="octopus-upload-banner" role="status">
                        <div className="octopus-upload-progress">
                            <div className="octopus-upload-progress-bar" />
                        </div>
                        <div className="octopus-upload-meta">
                            Uploading {attachments[0].name}
                            {attachments.length > 1 && ` (+${attachments.length - 1} more)`}
                        </div>
                    </div>
                )}

                {hasAttachments && (
                    <div className="octopus-chat-attachments">
                        {groupedAttachments.images.map((attachment) => (
                            <div key={attachment.id} className="octopus-attachment-preview">
                                <img
                                    src={attachment.previewUrl || ''}
                                    alt={attachment.name}
                                    className="octopus-attachment-thumbnail"
                                />
                                <button
                                    type="button"
                                    className="octopus-attachment-remove"
                                    onClick={() => handleRemoveAttachment(attachment.id)}
                                    aria-label="Remove image"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                        {groupedAttachments.videos.map((attachment) => (
                            <div key={attachment.id} className="octopus-attachment-preview">
                                <video
                                    src={attachment.previewUrl || ''}
                                    className="octopus-attachment-thumbnail octopus-attachment-video"
                                    controls
                                />
                                <button
                                    type="button"
                                    className="octopus-attachment-remove"
                                    onClick={() => handleRemoveAttachment(attachment.id)}
                                    aria-label="Remove video"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                        {groupedAttachments.documents.map((attachment) => (
                            <div key={attachment.id} className="octopus-attachment-file">
                                <div className="octopus-attachment-file-meta">
                                    <span className="octopus-attachment-file-icon" aria-hidden="true">📄</span>
                                    <div className="octopus-attachment-file-info">
                                        <span className="octopus-attachment-file-name" title={attachment.name}>
                                            {attachment.name}
                                        </span>
                                        <span className="octopus-attachment-file-size">
                                            {(attachment.file.size / 1024 / 1024).toFixed(2)} MB
                                        </span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="octopus-attachment-remove"
                                    onClick={() => handleRemoveAttachment(attachment.id)}
                                    aria-label="Remove document"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <textarea
                    ref={textAreaRef}
                    className={`octopus-chat-input textarea ${isEditing ? 'octopus-editing-input' : ''}`}
                    placeholder={placeholder}
                    value={value}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    disabled={isSending}
                    rows={isExpanded ? 6 : 2}
                />
            </div>

            <div className="octopus-chat-controls" >
                <div className="octopus-chat-actions-left" style={{ visibility: isEditing ? 'hidden' : 'visible' }}>
                    <button
                        type="button"
                        className="octopus-control-button octopus-icon-button bb-icon-camera-fill"
                        onClick={() => imageInputRef.current?.click()}
                    >
                        <span className="octopus-sr-only">Attach image</span>
                    </button>
                    <button
                        type="button"
                        className="octopus-control-button octopus-icon-button bb-icon-video-fill"
                        onClick={() => videoInputRef.current?.click()}
                    >
                        <span className="octopus-sr-only">Attach video</span>
                    </button>
                    <button
                        type="button"
                        className="octopus-control-button octopus-icon-button bb-icon-attach"
                        onClick={() => documentInputRef.current?.click()}
                    >
                        <span className="octopus-sr-only">Attach file</span>
                    </button>
                    <div className="octopus-gif-trigger" ref={gifTriggerRef}>
                        <button
                            type="button"
                            className={`octopus-control-button octopus-gif-button ${showGifPicker ? 'active' : ''}`}
                            onClick={toggleGifPicker}
                            disabled={!giphyApiKey}
                        >
                           <span className="octopus-gif-label">GIF</span>
                        </button>
                        {showGifPicker && (
                            <div className="octopus-gif-popover" ref={gifPopoverRef}>
                                {giphyApiKey ? (
                                    <>
                                        <div className="octopus-gif-search">
                                            <input
                                                type="search"
                                                value={gifQuery}
                                                onChange={(event) => setGifQuery(event.target.value)}
                                                placeholder="Search GIFs..."
                                                className="octopus-gif-search-input"
                                            />
                                        </div>
                                        <div className="octopus-gif-content">
                                            {gifLoading ? (
                                                <div className="octopus-gif-loading">Loading...</div>
                                            ) : gifError ? (
                                                <div className="octopus-gif-error">{gifError}</div>
                                            ) : gifResults.length === 0 ? (
                                                <div className="octopus-gif-empty">No GIFs found.</div>
                                            ) : (
                                                <div className="octopus-gif-grid">
                                                    {gifResults.map((gif) => {
                                                        const preview =
                                                            gif.images?.fixed_width_small?.url ||
                                                            gif.images?.fixed_width?.url ||
                                                            gif.images?.downsized_still?.url;
                                                        return (
                                                            <button
                                                                type="button"
                                                                key={gif.id}
                                                                className="octopus-gif-item"
                                                                onClick={() => handleGifSelect(gif)}
                                                                title={gif.title || 'GIF'}
                                                            >
                                                                {preview ? (
                                                                    <img src={preview} alt={gif.title || 'GIF'} loading="lazy" />
                                                                ) : (
                                                                    <span>GIF</span>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div className="octopus-gif-placeholder">
                                        <p>GIF support requires a GIPHY API key.</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="octopus-emoji-trigger" ref={emojiTriggerRef}>
                        <button
                            type="button"
                            className={`octopus-control-button octopus-icon-button bb-icon-smile ${showEmojiPicker ? 'active' : ''}`}
                            onClick={toggleEmojiPicker}
                        >
                            <span className="octopus-sr-only">Insert emoji</span>
                        </button>
                        {showEmojiPicker && (
                            <div className="octopus-emoji-popover" ref={emojiPopoverRef}>
                                <EmojiPicker
                                    onEmojiClick={(emojiData) => handleEmojiSelect(emojiData?.emoji)}
                                    lazyLoadEmojis
                                    previewConfig={{ showPreview: false }}
                                    searchDisabled
                                    width="100%"
                                    skinTonesDisabled
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className="octopus-chat-actions-right">
                    {/* <button
                        type="button"
                        className={`octopus-control-button ${isExpanded ? 'active' : ''}`}
                        onClick={() => setIsExpanded((prev) => !prev)}
                        aria-label={isExpanded ? 'Collapse input' : 'Expand input'}
                    >
                        <span className="octopus-expand-label">Aa</span>
                    </button> */}
                    
                    <button
                        type="button"
                        className={`octopus-control-button octopus-send-button octopus-icon-button bb-icon-plane ${isSending ? 'disabled' : ''}`}
                        onClick={handleSend}
                        disabled={isSending}
                    >
                        <span className="octopus-sr-only">Send message</span>
                    </button>
                </div>
            </div>

            <input
                type="file"
                accept="image/*"
                multiple
                ref={imageInputRef}
                style={{ display: 'none' }}
                onChange={(event) => handleFileSelection(event, 'image')}
            />
            <input
                type="file"
                accept="video/*"
                multiple
                ref={videoInputRef}
                style={{ display: 'none' }}
                onChange={(event) => handleFileSelection(event, 'video')}
            />
            <input
                type="file"
                multiple
                ref={documentInputRef}
                style={{ display: 'none' }}
                onChange={(event) => handleFileSelection(event, 'document')}
            />
        </div>
    );
};

export default ChatMessageInput;

